// Turn an approved character image into a saved, rigged wardrobe avatar.
//
// Shared by every avatar build in the app — the create flow (/welcome and
// "Create avatar"), the Describe-it creator and the Outfit Studio. The work runs
// server-side on Tripo (functions/api/tripo/avatar.ts): image-to-model v3.1 →
// rig v1.0 spec "tripo" → GLB stored on R2 → wardrobe row. No animations are
// generated per avatar: idle / walk / run / jump come from the shared Tripo
// library and are retargeted onto the new rig at load time.
//
// Stages shown to the user: "Creating your 3D model…", "Rigging…", "Saving…".

import { setActiveAvatar, type Avatar } from '../services/avatarService';
import { startAvatarBuild, waitForAvatarBuild, type BuildProgress } from '../services/tripoAvatarBuild';

export type BuildAvatarProgress = BuildProgress;

export async function buildAvatarFromImage(params: {
  /** Data URL or one of our own https image URLs. */
  imageDataUrl: string;
  name: string;
  creationParams?: Record<string, string>;
  setActive?: boolean;
  progress?: BuildAvatarProgress;
  /** Small picture remembered so a reload can show what is building. */
  previewImage?: string;
}): Promise<Avatar> {
  const { progress } = params;
  progress?.onStage?.('Creating your 3D model…');
  progress?.onProgress?.(1);
  const id = await startAvatarBuild({
    image: params.imageDataUrl,
    name: params.name,
    creationParams: params.creationParams,
    previewImage: params.previewImage,
  });
  return finishAvatarBuild(id, { setActive: params.setActive, progress });
}

/** Wait for a started (or resumed) build, then optionally equip it. */
export async function finishAvatarBuild(id: string, opts: { setActive?: boolean; progress?: BuildAvatarProgress } = {}): Promise<Avatar> {
  const avatar = await waitForAvatarBuild(id, opts.progress);
  if (opts.setActive) {
    await setActiveAvatar(avatar.id);
    avatar.is_active = true;
  }
  return avatar;
}
